import { Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthService } from '../../_services/auth.service';
import { User } from '../../_interfaces/user';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { FileService } from '../../_services/file.service';
import { Profile } from '../../_interfaces/profile';
import { UserSettings } from '../../_interfaces/user-settings.interface';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ApiService } from '../../_services/api.service';
import { LoadingSpinnerComponent } from '../../shared/components/loading-spinner/loading-spinner.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { resolveAvatarUrl } from '../../_helpers/user.helper';
import { langCodeFromLocale } from '../../_helpers/utils';

@Component({
  selector: 'app-settings-details',
  imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatButtonModule, MatIconModule, LoadingSpinnerComponent, TranslateModule],
  templateUrl: './details.component.html',
  styleUrl: './details.component.scss'
})
export class SettingsDetailsComponent implements OnDestroy {
  user: User | null = null;
  settingsForm: FormGroup;
  isLoadingDetails = false;

  isLegalOrg = false;
  specialties: string[] = [];
  filteredSpecialties: string[] = [];
  searchTerm: string = '';

  pendingAvatarFile: File | null = null;
  localPreviewUrl: string | null = null;
  isDragging = false;

  get displayAvatarUrl(): string | null {
    if (this.localPreviewUrl) {
      return this.localPreviewUrl;
    }
    return resolveAvatarUrl(this.settingsForm.get('avatarUrl')?.value);
  }

  constructor(
    private authService: AuthService,
    private fileService: FileService,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private apiService: ApiService,
    private translate: TranslateService
  ) {
    this.settingsForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: [''],
      specialty: [''],
      avatarUrl: ['']
    });
    this.user = this.authService.getUser();
  }

  ngOnInit(): void {
    this.isLegalOrg = this.user?.entity?.settings?.category === 'Legal';
    this.setForm();
    if (!this.isLegalOrg) {
      void this.loadSpecialties();
    }
  }

  setForm(): void {
    if (this.user) {
      this.settingsForm.reset();
      this.settingsForm.patchValue({
        firstName: this.user.profile?.first_name,
        lastName: this.user.profile?.last_name,
        email: this.user.email,
        specialty: this.user.settings?.copilotSpeciality,
        avatarUrl: this.user.profile?.avatar_url
      });
    }
  }

  async saveChanges(): Promise<void> {
    if (this.settingsForm.valid && this.settingsForm.dirty) {
      let message = this.translate.instant('User details saved successfully');
      const formValue = this.settingsForm.value;

      this.isLoadingDetails = true;
      try {
        // Upload pending avatar if exists
        if (this.pendingAvatarFile) {
          const params = {
            format: this.pendingAvatarFile.name.split('.').pop() || '',
            contentType: this.pendingAvatarFile.type || 'application/octet-stream',
            type: 'profile'
          };
          const preSignedUrl = await this.fileService.uploadImageAWS(params, this.pendingAvatarFile);
          await this.authService.updateUser({
            fileName: preSignedUrl.fileName
          });
          this.clearPendingAvatar();
        }

        // Update other user details
        this.user = await this.authService.updateUser({
          profile: {
            first_name: formValue.firstName,
            last_name: formValue.lastName
          } as Profile,
          settings: {
            copilotSpeciality: formValue.specialty,
          } as UserSettings
        } as User);
        this.setForm();
      } catch (e) {
        console.error('Failed to save user details:', e);
        message = this.translate.instant('Unable to save user details');
      } finally {
        this.isLoadingDetails = false;
      }
      
      this.snackBar.open(message, this.translate.instant('Close'), {
        duration: 2000,
        horizontalPosition: 'right',
        verticalPosition: 'bottom'
      });
    }
  }

  onFilesChanged(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.processFile(file);
    }
  }

  private processFile(file: File): void {
    this.pendingAvatarFile = file;

    if (this.localPreviewUrl) {
      URL.revokeObjectURL(this.localPreviewUrl);
    }
    this.localPreviewUrl = URL.createObjectURL(file);

    this.settingsForm.markAsDirty();
  }

  cancel(): void {
    this.clearPendingAvatar();
    this.setForm();
  }

  private async loadSpecialties(): Promise<void> {
    try {
      const items = await this.apiService.getSpecialities(langCodeFromLocale(this.translate.currentLang));
      this.specialties = Array.isArray(items) ? items : [];
    } catch {
      this.specialties = [];
    }

    // A speciality saved while another language was active is absent from the localised
    // list, so keep it as an option instead of leaving the field blank.
    const saved = this.user?.settings?.copilotSpeciality;
    if (saved && !this.specialties.includes(saved)) {
      this.specialties = [saved, ...this.specialties];
    }

    this.filteredSpecialties = [...this.specialties];
  }

  searchSpecialty(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchTerm = input.value;
    const searchLower = this.searchTerm.toLowerCase();
    this.filteredSpecialties = this.specialties.filter(specialty =>
      specialty.toLowerCase().includes(searchLower)
    );
  }

  clearSearch(event: Event): void {
    event.stopPropagation();
    this.searchTerm = '';
    this.filteredSpecialties = [...this.specialties];
  }

  onSearchKeyDown(event: KeyboardEvent): void {
    event.stopPropagation();
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const file = event.dataTransfer?.files[0];
    if (file && file.type.startsWith('image/')) {
      this.processFile(file);
    }
  }

  ngOnDestroy(): void {
    this.clearPendingAvatar();
  }

  private clearPendingAvatar(): void {
    if (this.localPreviewUrl) {
      URL.revokeObjectURL(this.localPreviewUrl);
    }
    this.pendingAvatarFile = null;
    this.localPreviewUrl = null;
  }
} 